// Het rooster-raster zelf: datumkop, optioneel een extra rij erboven (de "te plannen"-bak
// op Nog in te plannen), en de personen per rolgroep met hun blokken. Sleep-en-neerzet
// hoort erbij: alles wat op een dagcel valt, gaat via de acties van useRooster.
// Gebruikt door het Rooster (overzicht) en Nog in te plannen (droppunt voor de bak).
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Button, CircularProgress, Popover, Checkbox, FormControlLabel, Divider,
  TextField, InputAdornment, IconButton, Tooltip, Chip,
  ToggleButtonGroup, ToggleButton, Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import {
  Refresh as RefreshIcon, HelpOutline as HelpIcon, Search as SearchIcon, Sort as SortIcon, Close as CloseIcon, Tune as TuneIcon, ArrowDropDown as ArrowDropDownIcon,
} from '@mui/icons-material';
import { DndContext, DragOverlay } from '@dnd-kit/core';
import { effectiveTaskHours, DEFAULT_CAPACITY_RULES } from '../../utils/capacityCalc';
import CapaciteitsRegels from '../../ui-components/CapaciteitsRegels';
import { projectStijl, fmtHalfUren, fmtUren, weekTotaal } from './rooster';
import { DatumKop, PersoonRij, GroepKop, NAAM_BREEDTE } from './RoosterOnderdelen';
import SpeeltuinDialog from './SpeeltuinDialog';
import TaakPaneel from '../../ui-components/TaakPaneel/TaakPaneel';
import { GROEP_LABEL, isFreelancer, isStagiair } from './useRooster';
import { rolTellingen } from '../../utils/rollen';

const GEDEMPT = '#6b7280';
// Vaste lege waarden: een verse `{}` per render breekt de memo van PersoonRij.
const GEEN_BLOKKEN = { geplaatst: [], laneCount: 0 };
const GEEN_LOAD = { uren: {}, staat: {} };
const scrollStijl = { overflowY: 'auto', overflowX: 'hidden', scrollbarGutter: 'stable' };

/**
 * @param rooster       resultaat van useRooster
 * @param groepen       personen per rolgroep (groepeerPersonen)
 * @param kopLinks      inhoud linksboven in de datumkop (zoekveld, sortering)
 * @param extraBovenRij rij tussen datumkop en personen (bv. de te-plannen-bak)
 * @param selectedPerson / onSelectPerson  persoon markeren (en de rest dimmen)
 * @param onTaakOpen    eigen taakpaneel van de pagina (Dagstart); weggelaten = het gedeelde TaakPaneel hier
 * @param naamHint      hover-tekst op een persoonsnaam (weggelaten = de standaard selectie-uitleg)
 * @param ruim          (Dagstart) meer lucht tussen personen
 * @param toonRuimte    (Dagstart) dagtotaal ook als ruimte ("+3u"), niet alleen het tekort
 * @param betekenis     (Dagstart) kleur = soort werk + mijlpalen met versie (utils/roosterBetekenis)
 */
/** Breedte van de eerste kolom in de Dagstart per persoon — gelijk aan de lane-koppen (PersonTimeline). */
export const EEN_PERSOON_NAAM = 104;

/** De eerste kolom per persoon: alleen hoeveel ruimte er deze periode nog is. */
function RuimteCel({ ruimte }) {
  return (
    <Box data-testid="een-persoon-ruimte" sx={{ height: '100%', width: '100%', display: 'flex', alignItems: 'center', px: 1, borderRight: '1px solid #eceef1' }}>
      <Typography sx={{ fontSize: 11.5, fontWeight: 600, color: ruimte > 0.05 ? '#15803d' : '#6b7280' }}>
        {ruimte > 0.05 ? `${fmtUren(ruimte)}u vrij` : 'vol'}
      </Typography>
    </Box>
  );
}

export default function RoosterGrid({
  rooster, groepen, kopLinks, extraBovenRij = null, selectedPerson = null, onSelectPerson, sx,
  onTaakOpen = null, naamHint, ruim = false, toonRuimte = false, betekenis = false,
  eenPersoon = false,
}) {
  // Dagstart per persoon (Joris 4 nov, "voelt druk"): geen groepskop en een smalle eerste
  // kolom met alleen de ruimte — de naam staat al in de tab erboven. Die kolom is even breed
  // als de lane-koppen van de tijdlijn eronder, zodat beide in één raster staan.
  const naamBreedte = eenPersoon ? EEN_PERSOON_NAAM : NAAM_BREEDTE;
  const [open, setOpen] = useState({ Editor: true, Animator: true, Illustrator: true, Overig: true });
  // Klik op een taakblok → het gedeelde TaakPaneel (Rooster én Nog in te plannen).
  const [openTaak, setOpenTaak] = useState(null);
  const navigate = useNavigate();
  const {
    kolommen, sjabloon, dayISOs, rules, labels, belasting, blokkenPerPersoon, agendaPerPersoon, weekendPerPersoon = {},
    agendaLaden, agendaFout, agendaOnbekend = {}, overCell, activeTask, dnd, speeltuin,
  } = rooster;
  const zichtbaar = Object.values(groepen).reduce((s, l) => s + l.length, 0);
  // Stabiele props per rij (PersoonRij is gememoiseerd): één klikfunctie per persoon die via
  // een ref de actuele selectie leest, en een leeg totaal dat niet per render nieuw is.
  const selectieRef = useRef({ selectedPerson, onSelectPerson });
  selectieRef.current = { selectedPerson, onSelectPerson };
  const kiesCache = useRef(new Map());
  const kiesVoor = useCallback((id) => {
    if (!kiesCache.current.has(id)) {
      kiesCache.current.set(id, () => {
        const { selectedPerson: nu, onSelectPerson: kies } = selectieRef.current;
        kies?.(nu === id ? null : id);
      });
    }
    return kiesCache.current.get(id);
  }, []);
  const leegTotaal = useMemo(() => weekTotaal({}, {}, dayISOs, rules.hoursPerDay), [dayISOs, rules.hoursPerDay]);
  const taakOpen = onTaakOpen || setOpenTaak;

  return (
    <DndContext {...dnd}>
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', border: '1px solid #eceef1', borderRadius: 2, overflow: 'hidden', bgcolor: '#fff', ...sx }}>
        <Box sx={{ ...scrollStijl, overflowY: 'hidden', flexShrink: 0 }}>
          <DatumKop kolommen={kolommen} sjabloon={sjabloon} links={kopLinks} naamBreedte={naamBreedte} />
        </Box>
        {extraBovenRij}
        {/* overscroll contain: aan het eind van de lijst scrolt het wiel niet door naar de pagina —
            dan schoof de plakkende filterbalk over de datumkop en leek die afgeknipt (4 nov). */}
        <Box sx={{ ...scrollStijl, flex: 1, minHeight: 0, overscrollBehaviorY: 'contain' }}>
          {agendaLaden && (
            <Box sx={{ px: 1, py: 0.35, borderBottom: '1px solid #eceef1', display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <CircularProgress size={10} thickness={6} sx={{ color: GEDEMPT }} />
              <Typography sx={{ fontSize: 10.5, color: GEDEMPT }}>Agenda-uren uit Outlook laden — bezetting en volgorde worden zo bijgewerkt…</Typography>
            </Box>
          )}
          {!agendaLaden && agendaFout && (
            <Box role="status" sx={{ px: 1, py: 0.35, borderBottom: '1px solid #eceef1' }}>
              <Typography sx={{ fontSize: 10.5, color: GEDEMPT }}>Outlook-agenda kon niet geladen worden — meetings en afwezigheid tellen nu niet mee (agenda onbekend).</Typography>
            </Box>
          )}
          {zichtbaar === 0 && (
            <Typography sx={{ p: 2, fontSize: 12, color: GEDEMPT }}>Niemand gevonden binnen dit filter.</Typography>
          )}
          {Object.entries(groepen).map(([groupName, groupPersons]) => {
            if (groupPersons.length === 0) return null;
            const isOpen = eenPersoon || open[groupName] !== false;
            const ruimte = groupPersons.reduce((s, p) => s + (belasting[p.id]?.totaal.ruimte || 0), 0);
            return (
              <Box key={groupName}>
                {!eenPersoon && <GroepKop
                  label={GROEP_LABEL[groupName] || groupName}
                  aantal={groupPersons.length}
                  ruimte={ruimte}
                  open={isOpen}
                  onToggle={() => setOpen(prev => ({ ...prev, [groupName]: !isOpen }))}
                />}
                {isOpen && groupPersons.map(person => (
                  <PersoonRij
                    key={person.id}
                    person={person}
                    label={labels[person.id]}
                    kolommen={kolommen}
                    sjabloon={sjabloon}
                    blokken={blokkenPerPersoon[person.id] || GEEN_BLOKKEN}
                    agendaPerDag={agendaPerPersoon[person.id]}
                    weekendWerk={weekendPerPersoon[person.id]}
                    agendaOnbekend={agendaOnbekend[person.id] || null}
                    load={belasting[person.id]?.load || GEEN_LOAD}
                    totaal={belasting[person.id]?.totaal || leegTotaal}
                    rules={rules}
                    // Alleen de rij waar de sleep boven hangt krijgt de cel; de rest blijft staan.
                    overCell={overCell?.personId === person.id ? overCell : null}
                    selected={selectedPerson === person.id}
                    gedimd={!!selectedPerson && selectedPerson !== person.id}
                    onSelect={kiesVoor(person.id)}
                    onTaakOpen={taakOpen}
                    naamHint={naamHint}
                    ruim={ruim}
                    toonRuimte={toonRuimte}
                    betekenis={betekenis}
                    {...(eenPersoon ? {
                      naamKolom: `${naamBreedte}px`,
                      naamInhoud: <RuimteCel ruimte={belasting[person.id]?.totaal.ruimte || 0} />,
                    } : {})}
                  />
                ))}
              </Box>
            );
          })}
        </Box>
      </Box>

      <SleepVoorbeeld activeTask={activeTask} overCell={overCell} labels={labels} />

      {!onTaakOpen && (
      <TaakPaneel
        taak={openTaak}
        open={Boolean(openTaak)}
        onClose={() => setOpenTaak(null)}
        personen={rooster.persons}
        // Het paneel schrijft via schrijfTaak (planningcache vervalt); daarna het rooster stil verversen.
        onBijgewerkt={() => rooster.fetchData?.()}
        onVerwijderd={() => { setOpenTaak(null); rooster.fetchData?.(); }}
        onNaarProject={(pid) => { setOpenTaak(null); navigate(`/projects/${pid}`); }}
      />
      )}

      {speeltuin && (
        <SpeeltuinDialog
          info={speeltuin}
          persons={rooster.persons}
          existingTasks={rooster.existingTasks}
          vasteTaken={rooster.vasteBezetting?.[speeltuin.personId] || []}
          calendarHours={rooster.calendarHours}
          gedeeldeMails={rooster.gedeeldeMails}
          rules={rules}
          onClose={rooster.sluitSpeeltuin}
          onKlaar={rooster.speeltuinKlaar}
        />
      )}
    </DndContext>
  );
}

/**
 * Wat er onder de muis meereist tijdens een sleep: de taak (of het blok) en waar hij landt.
 * Gedeeld met de uitklap op Capaciteit. `labels` weggelaten = geen persoonsnaam in de bestemming.
 */
export function SleepVoorbeeld({ activeTask, overCell, labels = {} }) {
  return (
    <DragOverlay dropAnimation={null}>
      {activeTask && (
        <Box sx={{
          px: 1.25, py: 0.5, borderRadius: 1, maxWidth: 320,
          bgcolor: activeTask.deliverable ? '#ede9fe' : projectStijl(activeTask).vlak,
          color: projectStijl(activeTask).tekst, fontSize: 12, fontWeight: 600,
          boxShadow: `inset 3px 0 0 ${activeTask.deliverable ? '#7c3aed' : projectStijl(activeTask).streep}, 0 6px 16px rgba(15,23,42,0.18)`,
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', pointerEvents: 'none',
        }}>
          {activeTask.name} ({fmtHalfUren(activeTask.deliverable ? activeTask.hours : effectiveTaskHours(activeTask, DEFAULT_CAPACITY_RULES).val)}u)
          {overCell && ` → ${labels[overCell.personId] || ''} ${overCell.date.slice(5).split('-').reverse().join('-')}`}
        </Box>
      )}
    </DragOverlay>
  );
}

/**
 * "Weergave"-knop met rollen, freelancers, stagiairs, venster, uitleg, rekenregels en verversen.
 * `numWeken`/`onWeken` weglaten = geen vensterkeuze; `onToonStagiairs` weglaten = geen stagiairvinkje.
 */
export function RoosterWeergave({
  rooster, functionFilter, onFunctionFilter, toonFreelancers, onToonFreelancers, numWeken, onWeken,
  toonStagiairs = true, onToonStagiairs,
}) {
  const [anker, setAnker] = useState(null);
  const [regelsOpen, setRegelsOpen] = useState(false);
  const aantalFreelancers = rooster.persons.filter(isFreelancer).length;
  const aantalStagiairs = rooster.persons.filter(isStagiair).length;
  // Rollen uit de data: alleen wat bij iemand in beeld voorkomt (freelancers en stagiairs
  // alleen als die aan staan), met aantal; een aangevinkte rol blijft staan.
  const rollen = rolTellingen(
    rooster.persons.filter(p => (toonFreelancers || !isFreelancer(p)) && (toonStagiairs || !isStagiair(p))),
    { altijd: functionFilter },
  );
  const samenvatting = [
    functionFilter.length === 1 ? GROEP_LABEL[functionFilter[0]] : functionFilter.length > 1 ? `${functionFilter.length} rollen` : null,
    toonFreelancers ? '+ freelancers' : null,
    toonStagiairs ? null : 'zonder stagiairs',
    onWeken ? `${numWeken}w` : null,
  ].filter(Boolean).join(' · ');
  const wissel = (k) => onFunctionFilter(functionFilter.includes(k) ? functionFilter.filter(x => x !== k) : [...functionFilter, k]);
  const kop = { fontSize: 11, fontWeight: 700, color: GEDEMPT, textTransform: 'uppercase', letterSpacing: 0.4 };
  return (
    <>
      <Button
        size="small" variant="outlined" onClick={(e) => setAnker(e.currentTarget)}
        startIcon={<TuneIcon sx={{ fontSize: '16px !important' }} />} endIcon={<ArrowDropDownIcon />}
        aria-haspopup="true" aria-expanded={!!anker}
        sx={{ textTransform: 'none', fontWeight: 600, fontSize: 12, px: 1.25, py: 0.3, borderColor: '#cbd5e1', color: '#334155' }}
      >
        Weergave
        {samenvatting && <Box component="span" sx={{ ml: 0.75, fontWeight: 500, color: GEDEMPT }}>{samenvatting}</Box>}
      </Button>
      <Popover
        open={!!anker} anchorEl={anker} onClose={() => setAnker(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { p: 1.5, width: 260 } } }}
      >
        <Typography sx={{ ...kop, mb: 0.25 }}>Rollen</Typography>
        {rollen.map(g => (
          <FormControlLabel
            key={g.key}
            sx={{ display: 'flex', m: 0, '& .MuiFormControlLabel-label': { fontSize: 13 } }}
            control={<Checkbox size="small" sx={{ p: 0.5 }} checked={functionFilter.includes(g.key)} onChange={() => wissel(g.key)} />}
            label={<>{g.label} <Box component="span" sx={{ color: GEDEMPT }}>({g.aantal})</Box></>}
          />
        ))}
        <FormControlLabel
          sx={{ display: 'flex', m: 0, '& .MuiFormControlLabel-label': { fontSize: 13 } }}
          control={<Checkbox size="small" sx={{ p: 0.5 }} checked={toonFreelancers} onChange={() => onToonFreelancers(!toonFreelancers)} />}
          label={<>Freelancers <Box component="span" sx={{ color: GEDEMPT }}>({aantalFreelancers})</Box></>}
        />
        {onToonStagiairs && (
          <FormControlLabel
            sx={{ display: 'flex', m: 0, '& .MuiFormControlLabel-label': { fontSize: 13 } }}
            control={<Checkbox size="small" sx={{ p: 0.5 }} checked={toonStagiairs} onChange={() => onToonStagiairs(!toonStagiairs)} />}
            label={<>Stagiairs tonen <Box component="span" sx={{ color: GEDEMPT }}>({aantalStagiairs})</Box></>}
          />
        )}
        <Typography sx={{ fontSize: 11, color: GEDEMPT, mt: 0.25, mb: 1 }}>Niets aangevinkt bij rollen = iedereen.</Typography>
        {onWeken && (
          <>
            <Typography sx={{ ...kop, mb: 0.5 }}>Venster</Typography>
            <ToggleButtonGroup size="small" exclusive fullWidth value={numWeken} onChange={(e, v) => v && onWeken(v)}
              sx={{ '& .MuiToggleButton-root': { py: 0.35, fontSize: 12, fontWeight: 700, textTransform: 'none' } }}>
              {[1, 2, 4].map(n => <ToggleButton key={n} value={n}>{n} {n === 1 ? 'week' : 'weken'}</ToggleButton>)}
            </ToggleButtonGroup>
          </>
        )}
        <Divider sx={{ my: 1.25 }} />
        <Typography sx={{ fontSize: 11, color: GEDEMPT, lineHeight: 1.45, mb: 1 }}>
          Grijze vulling onderin een dag = afspraken en klein werk (hoogte = deel van de dag; details bij hover).
          Rood = meer werk dan uren; het rode getal is het tekort.
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button size="small" startIcon={<HelpIcon sx={{ fontSize: '16px !important' }} />} onClick={() => { setAnker(null); setRegelsOpen(true); }}
            sx={{ textTransform: 'none', fontSize: 12, color: '#334155' }}>
            Rekenregels
          </Button>
          <Button size="small" startIcon={<RefreshIcon sx={{ fontSize: '16px !important' }} />} onClick={() => { setAnker(null); rooster.verversAlles(); }}
            sx={{ textTransform: 'none', fontSize: 12, color: '#334155' }}>
            Verversen
          </Button>
        </Box>
      </Popover>
      <Dialog open={regelsOpen} onClose={() => setRegelsOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>Capaciteitsregels</DialogTitle>
        <DialogContent dividers>
          <CapaciteitsRegels onSaved={rooster.herlaadRegels} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRegelsOpen(false)} sx={{ textTransform: 'none' }}>Sluiten</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

/** Linksboven in de datumkop: één zoekveld (personen én taken) en de sortering. */
export function RoosterZoek({ zoekterm, onZoekterm, sortering, onSortering, placeholder = 'Zoek persoon of taak' }) {
  return (
    <>
      <TextField
        size="small" placeholder={placeholder} value={zoekterm}
        onChange={(e) => onZoekterm(e.target.value)}
        InputProps={{
          startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 16 }} /></InputAdornment>,
          endAdornment: zoekterm ? <IconButton size="small" aria-label="Zoekterm wissen" onClick={() => onZoekterm('')} sx={{ p: 0.25 }}><CloseIcon sx={{ fontSize: 14 }} /></IconButton> : null,
          sx: { fontSize: 12, height: 30, pl: 1 },
        }}
        sx={{ flex: 1, minWidth: 0 }}
      />
      <Tooltip title={sortering === 'ruimte' ? 'Gesorteerd op meeste ruimte — klik voor alfabetisch' : 'Alfabetisch — klik voor meeste ruimte eerst'}>
        <Chip
          icon={<SortIcon sx={{ fontSize: 14 }} />}
          label={sortering === 'ruimte' ? 'Ruimte' : 'A–Z'}
          size="small" onClick={() => onSortering(sortering === 'ruimte' ? 'naam' : 'ruimte')}
          sx={{ height: 26, fontSize: 12, fontWeight: 600 }}
        />
      </Tooltip>
    </>
  );
}
